import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, FileText, Sparkles, Eye, X } from 'lucide-react';

const PAGE_SIZE = 25;

// Todos os skips terminais do scheduler contam como "Pulada", nunca como falha.
const SKIPPED_STATUSES = new Set([
  'skipped_24h_window',
  'skipped_by_agent',
  'skipped_human_attendance',
  'skipped_ownership',
  'cancelled_on_reply',
]);

// Motivo legível do skip (o status É o motivo; o backend não grava error aqui).
const SKIP_REASON_LABELS = {
  skipped_24h_window: 'janela de 24h fechada',
  skipped_by_agent: 'IA decidiu não enviar',
  skipped_human_attendance: 'cliente em atendimento humano',
  skipped_ownership: 'contato de outro workspace',
  cancelled_on_reply: 'cancelado: o cliente respondeu',
};

// Erros técnicos conhecidos traduzidos (mesmo mapa do FollowupBadge) — o
// código cru fica no tooltip via title, o operador lê português na linha.
const FAIL_REASON_LABELS = {
  no_active_agent: 'workspace sem agente de IA ativo',
  agent_timeout: 'a IA excedeu o tempo de geração',
  empty_response: 'a IA devolveu resposta vazia',
  missing_instruction: 'mensagem sem instrução para a IA',
};
const describeFailError = (err) => FAIL_REASON_LABELS[String(err || '')] || err;

const HistoryTab = ({ history, colors, styles, onResetRecipient, isScheduledMessage = false }) => {
  const [page, setPage] = useState(1);
  const [aiDetail, setAiDetail] = useState(null);
  // Filtro por desfecho (pedido myCar 08/08: achar as FALHAS rápido).
  const [statusFilter, setStatusFilter] = useState('all'); // all|sent|failed|skipped

  // AI-generated entries indexed by recipient+day for the detail modal
  const aiEntriesByKey = useMemo(() => {
    const map = {};
    history.forEach(h => {
      if (!h.ai_generated) return;
      const recipient = typeof h.recipient === 'string' ? h.recipient : (h.recipient?.phone || String(h.recipient));
      const key = `${recipient}::${h.day}`;
      if (!map[key]) map[key] = [];
      map[key].push(h);
    });
    return map;
  }, [history]);

  // Consolidate: group by recipient + day
  const consolidatedRows = useMemo(() => {
    const groups = {};
    history.forEach(h => {
      // Ensure recipient is always a string (handle possible {phone, added_at} format)
      const recipient = typeof h.recipient === 'string' ? h.recipient : (h.recipient?.phone || String(h.recipient));
      const key = `${recipient}::${h.day}`;
      if (!groups[key]) {
        groups[key] = {
          recipient,
          day: h.day,
          total: 0,
          sent: 0,
          failed: 0,
          skipped: 0,
          expired: 0,
          pending: 0,
          hasAi: false,
          lastDate: h.sent_at,
          isTest: h.is_test,
        };
      }
      groups[key].total++;
      if (h.status === 'sent') groups[key].sent++;
      else if (SKIPPED_STATUSES.has(h.status)) {
        groups[key].skipped++;
        groups[key].lastSkipReason = SKIP_REASON_LABELS[h.status] || h.status;
      } else if (h.status === 'expired') {
        // Carimbo de vencimento do scheduler: NÃO é falha (a tentativa real,
        // se houve, está em outro registro do mesmo destinatário).
        groups[key].expired++;
      } else if (h.status === 'pending') {
        // Lock em andamento (ou órfão até o cleanup): estado transitório.
        groups[key].pending++;
      } else {
        groups[key].failed++;
        // Motivo da última falha — visível na linha (antes só dizia "Falhou").
        if (h.error) groups[key].lastError = h.error;
      }
      if (h.ai_generated) groups[key].hasAi = true;
      if (new Date(h.sent_at) > new Date(groups[key].lastDate)) {
        groups[key].lastDate = h.sent_at;
      }
    });

    // Mais recente primeiro: o operador procura "o que aconteceu hoje".
    return Object.values(groups).sort(
      (a, b) => new Date(b.lastDate) - new Date(a.lastDate)
    );
  }, [history]);

  // Mesma cascata do rótulo da linha, para chip e tabela nunca divergirem:
  // Falhou > Em andamento > Enviado > Pulada > Não processado.
  const classifyRow = (r) => {
    if (r.failed > 0) return 'failed';
    if (r.pending > 0) return 'pending';
    if (r.sent > 0) return 'sent';
    if (r.skipped > 0) return 'skipped';
    if (r.expired > 0) return 'expired';
    return 'failed';
  };

  const summary = useMemo(() => {
    const s = { total: consolidatedRows.length, sent: 0, failed: 0, skipped: 0, expired: 0, pending: 0 };
    consolidatedRows.forEach((r) => { s[classifyRow(r)]++; });
    return s;
  }, [consolidatedRows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'all') return consolidatedRows;
    return consolidatedRows.filter((r) => classifyRow(r) === statusFilter);
  }, [consolidatedRows, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filteredRows.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <>
      {/* Header + resumo clicável (filtra por desfecho) */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px',
        fontSize: '13px', color: colors.textSecondary, flexWrap: 'wrap',
      }}>
        <FileText size={14} />
        <span>{consolidatedRows.length} registro{consolidatedRows.length !== 1 ? 's' : ''}</span>
        {[
          { key: 'all', label: `Todos (${summary.total})`, color: colors.textSecondary },
          { key: 'sent', label: `Enviados (${summary.sent})`, color: '#22c55e' },
          { key: 'failed', label: `Falhas (${summary.failed})`, color: '#ef4444' },
          { key: 'skipped', label: `Pulados (${summary.skipped})`, color: '#9ca3af' },
          // Estados raros: o chip só aparece quando existe registro.
          ...(summary.expired > 0
            ? [{ key: 'expired', label: `Não processados (${summary.expired})`, color: '#f59e0b' }]
            : []),
          ...(summary.pending > 0
            ? [{ key: 'pending', label: `Em andamento (${summary.pending})`, color: '#3b82f6' }]
            : []),
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => { setStatusFilter(f.key); setPage(1); }}
            style={{
              fontSize: '11px', padding: '3px 10px', borderRadius: 999,
              border: `1px solid ${statusFilter === f.key ? f.color : colors.borderColor}`,
              backgroundColor: statusFilter === f.key ? `${f.color}18` : 'transparent',
              color: f.color, cursor: 'pointer', fontWeight: 600,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filteredRows.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: colors.textSecondary, fontSize: '14px',
        }}>
          Nenhum envio registrado ainda.
        </div>
      ) : (
        <div style={{
          backgroundColor: colors.bgPrimary, borderRadius: '12px',
          border: `1px solid ${colors.borderColor}`, overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Destinatario
                </th>
                {!isScheduledMessage && (
                  <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                    Dia
                  </th>
                )}
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Msgs
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Status
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Teste
                </th>
                <th style={{ textAlign: 'right', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Data
                </th>
                <th style={{ textAlign: 'right', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((row, i) => {
                // Cascata alinhada com classifyRow; "Parcial" quando houve
                // envio junto de falha/pulo no mesmo destinatário.
                const kind = classifyRow(row);
                let statusLabel, statusColor;
                if (kind === 'failed') {
                  statusLabel = row.sent > 0 ? 'Parcial' : 'Falhou';
                  statusColor = row.sent > 0 ? '#f59e0b' : '#ef4444';
                } else if (kind === 'pending') { statusLabel = 'Em andamento'; statusColor = '#3b82f6'; }
                else if (kind === 'sent') {
                  statusLabel = row.skipped > 0 ? 'Parcial' : 'Enviado';
                  statusColor = row.skipped > 0 ? '#f59e0b' : '#22c55e';
                }
                else if (kind === 'skipped') { statusLabel = 'Pulada'; statusColor = '#9ca3af'; }
                else { statusLabel = 'Não processado'; statusColor = '#f59e0b'; }
                // Motivo na linha: falha real > motivo do pulo > venceu sem tentativa.
                const rowReason = (kind === 'failed' && row.lastError)
                  ? describeFailError(row.lastError)
                  : (kind === 'skipped' && row.lastSkipReason)
                  ? row.lastSkipReason
                  : (kind === 'expired')
                  ? 'venceu sem tentativa de envio'
                  : null;
                const reasonColor = kind === 'failed' ? '#ef4444' : (kind === 'expired' ? '#f59e0b' : '#9ca3af');
                const aiKey = `${row.recipient}::${row.day}`;
                const aiEntries = aiEntriesByKey[aiKey] || [];
                return (
                  <tr key={`${row.recipient}-${row.day}-${i}`} style={{ borderBottom: `1px solid ${colors.borderColor}20` }}>
                    <td style={{ padding: '10px 14px', color: colors.textPrimary, fontFamily: 'monospace', fontSize: '12px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {row.hasAi && <Sparkles size={11} color="#a855f7" />}
                        {row.recipient}
                      </span>
                    </td>
                    {!isScheduledMessage && (
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textPrimary }}>
                        Dia {row.day}
                      </td>
                    )}
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textSecondary }}>
                      {row.sent}/{row.total}
                      {row.skipped > 0 && (
                        <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '4px' }}>
                          ({row.skipped} pulada{row.skipped > 1 ? 's' : ''})
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span
                        title={rowReason || undefined}
                        style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                          backgroundColor: `${statusColor}20`, color: statusColor,
                        }}
                      >
                        {statusLabel}
                      </span>
                      {rowReason && (
                        <div style={{
                          fontSize: '10px', color: reasonColor, marginTop: 3,
                          maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', marginLeft: 'auto', marginRight: 'auto',
                        }} title={rowReason}>
                          {rowReason}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textSecondary }}>
                      {row.isTest ? 'Sim' : '-'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: colors.textSecondary, fontSize: '12px' }}>
                      {new Date(row.lastDate).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                        {aiEntries.length > 0 && (
                          <button
                            onClick={() => setAiDetail(aiEntries)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'none', border: `1px solid ${colors.borderColor}`,
                              borderRadius: '6px', padding: '4px 8px',
                              color: '#a855f7', cursor: 'pointer',
                            }}
                            title="Ver instrução e mensagem gerada pela IA"
                          >
                            <Eye size={12} />
                          </button>
                        )}
                        <button
                          onClick={() => onResetRecipient(row.recipient)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'none', border: `1px solid ${colors.borderColor}`,
                            borderRadius: '6px', padding: '4px 8px',
                            color: colors.textSecondary, cursor: 'pointer',
                          }}
                          title="Resetar histórico deste número"
                        >
                          <RotateCcw size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* AI detail modal */}
      {aiDetail && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }} onClick={() => setAiDetail(null)}>
          <div style={{
            backgroundColor: colors.bgPrimary, borderRadius: '16px',
            width: '100%', maxWidth: '560px', maxHeight: '85vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
            }}>
              <h3 style={{
                margin: 0, fontSize: '15px', fontWeight: 600, color: colors.textPrimary,
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <Sparkles size={16} color="#a855f7" /> Detalhes da Mensagem com IA
              </h3>
              <button onClick={() => setAiDetail(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: colors.textSecondary, padding: '6px', borderRadius: '8px',
              }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {aiDetail.map((entry, i) => {
                const skipReason = entry.ai_skip_reason;
                const labelByStatus = {
                  sent: 'Enviado',
                  skipped_24h_window: 'Pulada (janela 24h fechada)',
                  skipped_by_agent: 'Pulada pela IA',
                  skipped_human_attendance: 'Pulada (cliente em atendimento humano)',
                  skipped_ownership: 'Pulada (contato de outro workspace)',
                  expired: 'Não processado (venceu sem tentativa)',
                  pending: 'Em andamento',
                  failed: 'Falhou',
                  failed_no_response: 'Falhou (sem resposta)',
                };
                const statusLbl = labelByStatus[entry.status] || entry.status;
                return (
                  <div key={i} style={{
                    border: `1px solid ${colors.borderColor}`, borderRadius: '10px', padding: '12px',
                    backgroundColor: colors.bgSecondary,
                  }}>
                    <div style={{ fontSize: '11px', color: colors.textTertiary, marginBottom: '8px' }}>
                      {new Date(entry.sent_at).toLocaleString('pt-BR')}
                      {' · '}
                      <span style={{ color: entry.status === 'sent' ? '#22c55e' : (skipReason ? '#9ca3af' : '#ef4444') }}>
                        {statusLbl}
                      </span>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, fontWeight: 600, marginBottom: '4px' }}>
                        Instrução
                      </div>
                      <pre style={{
                        margin: 0, fontFamily: 'inherit', fontSize: '12px', color: colors.textPrimary,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>{entry.ai_instruction || '(não registrada)'}</pre>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, fontWeight: 600, marginBottom: '4px' }}>
                        Mensagem gerada
                      </div>
                      {entry.ai_resolved_text ? (
                        <pre style={{
                          margin: 0, fontFamily: 'inherit', fontSize: '12px', color: colors.textPrimary,
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          background: colors.bgPrimary, padding: '8px', borderRadius: '6px',
                        }}>{entry.ai_resolved_text}</pre>
                      ) : (
                        <span style={{ fontSize: '12px', color: colors.textTertiary, fontStyle: 'italic' }}>
                          {skipReason === '24h_window'
                            ? 'Não enviada — janela 24h fechada (não foi gerada).'
                            : skipReason === 'agent_decided'
                            ? 'A IA decidiu não enviar e usou a tool ignorar_mensagem.'
                            : 'Mensagem não gerada.'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '12px', marginTop: '16px',
        }}>
          <button
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              ...styles.btn, ...styles.btnOutline, padding: '6px 12px', fontSize: '12px',
              opacity: currentPage === 1 ? 0.4 : 1,
              cursor: currentPage === 1 ? 'default' : 'pointer',
            }}
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <span style={{ fontSize: '13px', color: colors.textSecondary }}>
            {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            style={{
              ...styles.btn, ...styles.btnOutline, padding: '6px 12px', fontSize: '12px',
              opacity: currentPage === totalPages ? 0.4 : 1,
              cursor: currentPage === totalPages ? 'default' : 'pointer',
            }}
          >
            Próximo <ChevronRight size={14} />
          </button>
        </div>
      )}
    </>
  );
};

export default HistoryTab;
